import React, { useEffect, useState } from 'react';
import { Plus, Save, Trash2, Loader2, Sparkles, ArrowUp, ArrowDown } from 'lucide-react';
import { toast } from 'sonner';
import { api } from '../../api/client';
import UploadField from './UploadField';

const inp = 'w-full bg-navy-dark border border-white/20 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-pink-brand';
const lbl = 'text-white/60 text-[10px] font-bold uppercase tracking-widest mb-1 block';
const Card = ({ children }) => <div className="glass-card p-5">{children}</div>;
const Loader = () => <div className="text-center py-8"><Loader2 className="text-pink-brand animate-spin mx-auto" /></div>;

const ICONS = ['Sparkles', 'Trophy', 'Flame'];

export default function FinaleEditor() {
  const [data, setData] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.getFinale().then((d) => setData({
      label: d.label || 'Serata della Finale',
      title_part1: d.title_part1 || 'Domenica',
      title_part2: d.title_part2 || '31 Maggio 2026',
      stadio: d.stadio || '',
      indirizzo: d.indirizzo || '',
      bg_image: d.bg_image || '',
      programma_title: d.programma_title || 'Programma Serata Conclusiva',
      programma: d.programma || [],
    })).catch(() => setData({
      label: 'Serata della Finale', title_part1: 'Domenica', title_part2: '31 Maggio 2026',
      stadio: '', indirizzo: '', bg_image: '', programma_title: 'Programma Serata Conclusiva', programma: [],
    }));
  }, []);

  if (!data) return <Loader />;
  const set = (k, v) => setData((d) => ({ ...d, [k]: v }));

  const addEv = () => set('programma', [...data.programma, { ora: '', evento: '', icon: 'Sparkles' }]);
  const updEv = (i, k, v) => { const next = [...data.programma]; next[i] = { ...next[i], [k]: v }; set('programma', next); };
  const rmEv = (i) => set('programma', data.programma.filter((_, idx) => idx !== i));
  const move = (i, dir) => {
    const j = i + dir; if (j < 0 || j >= data.programma.length) return;
    const next = [...data.programma]; [next[i], next[j]] = [next[j], next[i]];
    set('programma', next);
  };

  const save = async () => {
    setSaving(true);
    try { await api.saveFinale(data); toast.success('Serata Finale salvata'); }
    catch (e) { toast.error(e?.message || 'Errore salvataggio'); }
    setSaving(false);
  };

  return (
    <div className="space-y-4" data-testid="finale-editor">
      <Card>
        <div className="flex items-center justify-between mb-3">
          <h2 className="text-white font-bold text-lg flex items-center gap-2"><Sparkles size={18} className="text-pink-brand" /> Banner "Serata Finale"</h2>
          <button onClick={save} disabled={saving} className="btn-primary !py-2 !px-5" data-testid="finale-save-button"><Save size={14} /> {saving ? 'Salvo…' : 'Salva tutto'}</button>
        </div>
        <p className="text-white/60 text-xs mb-3">Sezione visibile in homepage. Tutto è editabile da qui.</p>

        <div className="grid sm:grid-cols-2 gap-3">
          <div><label className={lbl}>Etichetta in alto</label><input className={inp} value={data.label} onChange={(e) => set('label', e.target.value)} placeholder="Serata della Finale" data-testid="finale-label-input" /></div>
          <div className="grid grid-cols-2 gap-3">
            <div><label className={lbl}>Titolo · prima parte</label><input className={inp} value={data.title_part1} onChange={(e) => set('title_part1', e.target.value)} placeholder="Domenica" /></div>
            <div><label className={lbl}>Titolo · evidenziata (rosa)</label><input className={inp} value={data.title_part2} onChange={(e) => set('title_part2', e.target.value)} placeholder="31 Maggio 2026" /></div>
          </div>
          <div><label className={lbl}>Stadio / luogo</label><input className={inp} value={data.stadio} onChange={(e) => set('stadio', e.target.value)} placeholder="Stadio Comunale Simonetta Lamberti" /></div>
          <div><label className={lbl}>Indirizzo</label><input className={inp} value={data.indirizzo} onChange={(e) => set('indirizzo', e.target.value)} placeholder="Cava de' Tirreni (SA)" /></div>
        </div>

        <div className="mt-3">
          <UploadField label="Immagine sfondo (opzionale)" accept="image/*" folder="finale" value={data.bg_image} onChange={(v) => set('bg_image', v)} />
        </div>
      </Card>

      <Card>
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-white font-bold">Programma serata</h3>
          <button onClick={addEv} className="btn-primary !py-2 !px-4 !text-sm" data-testid="finale-add-event"><Plus size={14} /> Aggiungi evento</button>
        </div>
        <div className="mb-3"><label className={lbl}>Titolo del riquadro programma</label><input className={inp} value={data.programma_title} onChange={(e) => set('programma_title', e.target.value)} /></div>
        <div className="space-y-2">
          {data.programma.map((p, i) => (
            <div key={i} className="bg-navy-dark border border-white/10 rounded-lg p-3 grid sm:grid-cols-12 gap-2 items-end">
              <div className="sm:col-span-2"><label className={lbl}>Ora</label><input className={inp} value={p.ora || ''} onChange={(e) => updEv(i, 'ora', e.target.value)} placeholder="20:30" /></div>
              <div className="sm:col-span-7"><label className={lbl}>Evento</label><input className={inp} value={p.evento || ''} onChange={(e) => updEv(i, 'evento', e.target.value)} placeholder="Cerimonia di premiazione" /></div>
              <div className="sm:col-span-2"><label className={lbl}>Icona</label>
                <select className={inp} value={p.icon || 'Sparkles'} onChange={(e) => updEv(i, 'icon', e.target.value)}>
                  {ICONS.map((ic) => <option key={ic} value={ic}>{ic}</option>)}
                </select>
              </div>
              <div className="sm:col-span-1 flex gap-1 justify-end">
                <button onClick={() => move(i, -1)} disabled={i === 0} className="p-1.5 text-white/60 hover:text-pink-brand disabled:opacity-30" title="Su"><ArrowUp size={14} /></button>
                <button onClick={() => move(i, 1)} disabled={i === data.programma.length - 1} className="p-1.5 text-white/60 hover:text-pink-brand disabled:opacity-30" title="Giù"><ArrowDown size={14} /></button>
                <button onClick={() => rmEv(i)} className="p-1.5 text-white/60 hover:text-red-400" title="Elimina"><Trash2 size={14} /></button>
              </div>
            </div>
          ))}
          {data.programma.length === 0 && <p className="text-white/50 text-sm text-center py-4">Nessun evento. Clicca "Aggiungi evento" per iniziare.</p>}
        </div>
      </Card>

      <button onClick={save} disabled={saving} className="btn-primary"><Save size={14} /> {saving ? 'Salvo…' : 'Salva tutto'}</button>
    </div>
  );
}
